import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useTheme } from '@mui/material/styles';
import { Grid, Typography, Box, Badge, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import MainCard from 'components/Cards/MainCard';
import SubCard from 'components/Cards/SubCard';
import { gridSpacing } from 'utils/constants';
import floppyImg from 'assets/images/floppydisk.png';
import windowsImg from 'assets/images/windows.png';
import windowsMSIImg from 'assets/images/windows_msi.png';
import macOSImg from 'assets/images/macOS.png';
import applianceImg from 'assets/images/appliance.png';
import txtImg from 'assets/images/txt.png';
import sccmImg from 'assets/images/sccm.png';
import pdfImg from 'assets/images/pdf.png';
import docImg from 'assets/images/doc.png';
import DownloadFileApiHelper from 'utils/downloadFileApiHelper';
import ApiHelper, { getCsrfToken } from 'utils/apiHelper';
import EditVideoDialog from './EditVideoDialog';

const ManagementReference = () => {
    const theme = useTheme();
    const { userInfo } = useSelector((state) => state.auth);
    const toastId = useRef(null);
    const manageOrganizationData = useSelector((state) => state.management.organization?.selected);
    const [pluginData, setPluginData] = useState({
        appliance: true,
        sccm: false
    });
    const [videoData, setVideoData] = useState([]);
    const [installerData, setInstallerData] = useState([]);
    const [plugData, setPlugData] = useState([]);
    const [pdfData, setPdfData] = useState([]);
    const [configData, setConfigData] = useState([]);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedRef, setSelectedRef] = useState(null);
    const [editableVideos, setEditableVideos] = useState([]);
    const [type, setType] = useState(null);
    const [limitFields, setLimitFields] = useState({});
    // const [formData, setFormdata] = useState({});
    useEffect(() => {
        getPluginReference();
        getReferences();
    }, []);

    const getPluginReference = () => {
        ApiHelper.get('/api/management/check_reference_plugin/')
            .then((res) => setPluginData(res.data))
            .catch((err) => {
                if (err.response.status !== 401) {
                    toast.error('Failed to get the reference plugin data');
                }
            });
    };

    const getReferences = () => {
        ApiHelper.get('/api/management/references/')
            .then((res) => {
                setVideoData(res.data['Video']);
                setInstallerData(res.data['Installer']);
                setPlugData(res.data['Plug-In']);
                setPdfData(res.data['PDF']);
                setLimitFields(res.data['inputFields']);
            })
            .catch((err) => {
                if (err.response.status !== 401) {
                    toast.error('Failed to get the reference video data');
                }
            });
    };

    const handleIsEdit = () => {
        setIsEdit(!isEdit);
    };

    const openInstallerTab = (e) => {
        setType('Installer');
        if (isEdit) {
            const installer = installerData.find((installer) => installer['id'] == e.target.id);
            if (installer) {
                setSelectedRef(installer);
            } else {
                toast.warning('This Installer is not editable');
            }
            return;
        }
        handleDownloadFile(e);
    };

    const openPlugTab = (e) => {
        setType('Plug-In');
        if (isEdit) {
            const plug = plugData.find((plug) => plug['id'] == e.target.id);
            if (plug) {
                setSelectedRef(plug);
            } else {
                toast.warning('This Plug-In is not editable');
            }
            return;
        }
        handleDownloadFile(e);
    };

    const openPdfTab = (e) => {
        setType('PDF');
        if (isEdit) {
            const pdf = pdfData.find((pdf) => pdf['id'] == e.target.id);
            if (pdf) {
                setSelectedRef(pdf);
            } else {
                toast.warning('This PDF is not editable');
            }
            return;
        }
        handleDownloadFile(e);
    };

    const openConfigTab = (e) => {
        setType('ConfigFile');
        if (isEdit) {
            const config = configData.find((config) => config['id'] === e.target.id);
            if (config) {
                setSelectedRef(config);
            } else {
                toast.warning('This ConfigFile is not editable');
            }
        }
    };

    const openVideoTab = (e) => {
        setType('Video');
        if (isEdit) {
            const video = videoData.find((video) => String(video['id']) === e.target.id);
            if (video) {
                setSelectedRef(video);
            } else {
                toast.warning('This video is not editable');
            }
            return;
        }

        ApiHelper.post('/api/management/reference_video_url/', {
            file_name: e.target.name,
            type: e.target.alt
        })
            .then((res) => {
                if (res.status === 200) {
                    setVideoData((filterVideoData) =>
                        filterVideoData.map(
                            (item) => {
                                return item.id === res.data.id
                                    ? { ...item, is_new: 0 } // Update the matching row
                                    : item;
                            } // Keep other rows unchanged
                        )
                    );
                    window.open(res.data.url, '_blank');
                }
            })
            .catch((err) => {
                if (err.response.status !== 401) {
                    console.log(err.response.message);
                    toast.error('Failed to get the reference video');
                }
            });
    };

    const handleUpdateReference = async (formData) => {
        try {
            formData.append('refType', type);
            const res = await ApiHelper.put(`/api/management/references/${selectedRef.id}/`, formData, { 'X-CSRFToken': getCsrfToken() });
            if (res.data.status == 'error') {
                toast.error(res.data.message);
                return;
            } else {
                getReferences();
                setSelectedRef(null);
                toast.success('Reference updated successfully');
            }
        } catch (error) {
            console.log(error);
            toast.error('Failed to update Reference');
        }
    };
    const handleDeleteReference = async () => {
        try {
            await ApiHelper.delete(`/api/management/references/${selectedRef.id}/`, { 'X-CSRFToken': getCsrfToken() });
            getReferences();
            setSelectedRef(null);
            toast.success('Reference deleted successfully');
        } catch (error) {
            console.log(error);
            toast.error('Failed to deleted Reference');
        }
    };

    const handleSaveReference = async (formData) => {
        try {
            formData.append('refType', type);
            const res = await ApiHelper.post(`/api/management/references/`, formData, {
                'X-CSRFToken': getCsrfToken()
            });
            if (res.data.status == 'error') {
                toast.error(res.data.message);
                return;
            } else {
                getReferences();
                setSelectedRef(null);
                toast.success('Reference Saved successfully');
            }
        } catch (error) {
            console.log(error);
            toast.error('Failed to Saved Reference');
        }
    };

    const handleAddNew = (e, type) => {
        setSelectedRef({});
        setType(type);
    };

    const handleDownloadFile = (e) => {
        toastId.current = toast.loading('Downloading, please wait...');
        DownloadFileApiHelper.post(
            '/api/management/download_reference_file/',
            {
                data: {
                    file_name: e.target.name,
                    type: e.target.alt
                },
                responseType: 'blob'
            },
            {}
        )
            .then((response) => {
                getReferences();
                let url = window.URL.createObjectURL(new Blob([response.data]));
                let filename;
                let a = document.createElement('a');
                a.href = url;
                filename = response.headers['content-disposition'].split('filename=')[1];
                a.download = filename;
                a.click();
                toast.update(toastId.current, {
                    render: 'Successfully downloaded',
                    type: 'success',
                    isLoading: false,
                    autoClose: 5000
                });
            })
            .catch((err) => {
                let content;
                if (err.response.status === 400) {
                    content = 'Failed! Could not find this file';
                    toast.error(``);
                } else {
                    content = 'Failed to download the file';
                }
                toast.update(toastId.current, {
                    render: content,
                    type: 'error',
                    isLoading: false,
                    autoClose: 5000
                });
            });
    };

    return (
        <MainCard
            title="Reference"
            cardHeaderTitle={manageOrganizationData?.orgid ? manageOrganizationData.name : userInfo.user.orgid.name}
            cardHeaderShow={true}
        >
            <Grid container spacing={gridSpacing}>
                {(userInfo.admin_permissions.root_admin || userInfo.admin_permissions.server_admin) && (
                    <Grid item xs={12} sx={{ textAlign: 'right' }}>
                        <Button variant="contained" color="primary" onClick={handleIsEdit}>
                            <EditIcon />
                            {isEdit ? 'Done' : 'Edit'}
                        </Button>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <SubCard title="Installer Downloads">
                        <Grid container>
                            <Grid item xs={12 / (5 + installerData.length + (isEdit ? 1 : 0))} sx={{ textAlign: 'center' }}>
                                <Box onClick={handleDownloadFile} className="reference">
                                    <img src={windowsImg} alt="Installer" name="windows" width="80" />
                                    <Typography>Client (exe)</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12 / (5 + installerData.length + (isEdit ? 1 : 0))} sx={{ textAlign: 'center' }}>
                                <Box onClick={handleDownloadFile} className="reference">
                                    <img src={windowsMSIImg} alt="Installer" name="windows_msi" width="80" />
                                    <Typography>Client (msi)</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12 / (5 + installerData.length + (isEdit ? 1 : 0))} sx={{ textAlign: 'center' }}>
                                <Box onClick={handleDownloadFile} className="reference">
                                    <img src={macOSImg} alt="Installer" name="macos" width="80" />
                                    <Typography>PKG</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12 / (5 + installerData.length + (isEdit ? 1 : 0))} sx={{ textAlign: 'center' }}>
                                <Box onClick={handleDownloadFile} className="reference">
                                    <img src={floppyImg} alt="Installer" name="wakeapp" width="80" />
                                    <Typography>WakeApp (exe)</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12 / (5 + installerData.length + (isEdit ? 1 : 0))} sx={{ textAlign: 'center' }}>
                                <Box onClick={handleDownloadFile} className="reference">
                                    <img src={txtImg} alt="ConfigFile" name="chromebook" width="55" />
                                    <Typography>Chrome </Typography>
                                </Box>
                            </Grid>
                            {installerData.map((installer, key) => (
                                <Grid item xs={12 / (5 + installerData.length + (isEdit ? 1 : 0))} sx={{ textAlign: 'center' }} key={key}>
                                    <Badge
                                        badgeContent="NEW"
                                        color="error"
                                        invisible={!installer['is_new']}
                                        sx={{
                                            '& .MuiBadge-badge': {
                                                fontSize: '0.8rem',
                                                height: '24px',
                                                minWidth: '45px'
                                            }
                                        }}
                                    >
                                        <Box
                                            onClick={openInstallerTab}
                                            className="reference"
                                            sx={{
                                                ...(isEdit && {
                                                    border: '3px dotted'
                                                })
                                            }}
                                        >
                                            <img
                                                src={installer['icon_url']}
                                                alt="Installer"
                                                id={installer['id']}
                                                name={installer['shortname']}
                                                width="55"
                                            />
                                            <Typography>{installer['shortname']}</Typography>
                                        </Box>
                                    </Badge>
                                </Grid>
                            ))}
                            {isEdit && (
                                <Grid
                                    item
                                    xs={12 / (5 + installerData.length + 1)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <Box
                                        onClick={(e) => handleAddNew(e, 'Installer')}
                                        className="reference"
                                        sx={{
                                            border: '3px dotted',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '70px',
                                            width: '70px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <AddIcon fontSize="large" color="primary" name="Installer" />
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </SubCard>
                </Grid>
                <Grid item xs={6}>
                    <SubCard title="Plug-in Downloads">
                        <Grid container>
                            {pluginData.sccm && (
                                <Grid
                                    item
                                    xs={Object.values(pluginData).filter(Boolean).length + plugData.length}
                                    sx={{ textAlign: 'center' }}
                                >
                                    <Box onClick={handleDownloadFile} className="reference">
                                        <img src={sccmImg} alt="Plug-In" name="sccm_plugin" width="70" />
                                    </Box>
                                </Grid>
                            )}
                            {pluginData.appliance && (
                                <Grid
                                    item
                                    xs={12 / (Object.values(pluginData).filter(Boolean).length + plugData.length + (isEdit ? 1 : 0))}
                                    sx={{ textAlign: 'center' }}
                                >
                                    <Box onClick={handleDownloadFile} className="reference">
                                        <img src={applianceImg} alt="Plug-In" name="appliance" width="70" />
                                    </Box>
                                </Grid>
                            )}
                            {pluginData.appliance === false && pluginData.sccm === false && plugData.length <= 0 && (
                                <Typography color={theme.palette.secondary.main}>
                                    There are no plug-ins available for this organization
                                </Typography>
                            )}
                            {plugData.map((plug, key) => (
                                <Grid
                                    item
                                    xs={12 / (Object.values(pluginData).filter(Boolean).length + plugData.length + (isEdit ? 1 : 0))}
                                    sx={{ textAlign: 'center' }}
                                    key={key}
                                >
                                    <Badge
                                        badgeContent="NEW"
                                        color="error"
                                        invisible={!plug['is_new']}
                                        sx={{
                                            '& .MuiBadge-badge': {
                                                fontSize: '0.8rem',
                                                height: '24px',
                                                minWidth: '45px'
                                            }
                                        }}
                                    >
                                        <Box
                                            onClick={openPlugTab}
                                            className="reference"
                                            sx={{
                                                ...(isEdit && {
                                                    border: '3px dotted'
                                                })
                                            }}
                                        >
                                            <img src={plug['icon_url']} alt="Plug-In" id={plug['id']} name={plug['shortname']} width="60" />
                                            <Typography>{plug['shortname']}</Typography>
                                        </Box>
                                    </Badge>
                                </Grid>
                            ))}
                            {isEdit && (
                                <Grid
                                    item
                                    xs={12 / (Object.values(pluginData).filter(Boolean).length + plugData.length + 1)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <Box
                                        onClick={(e) => handleAddNew(e, 'Plug-In')}
                                        className="reference"
                                        sx={{
                                            border: '3px dotted',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '70px',
                                            width: '70px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <AddIcon fontSize="large" color="primary" name="Plug-In" />
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </SubCard>
                </Grid>
                <Grid item xs={6}>
                    <SubCard title="Training Videos" sx={{ height: '100%' }}>
                        <Grid container>
                            {videoData.map((video, key) => (
                                <Grid item xs={12 / (videoData.length + (isEdit ? 1 : 0))} sx={{ textAlign: 'center' }} key={key}>
                                    <Badge
                                        badgeContent="NEW"
                                        color="error"
                                        invisible={!video['is_new']}
                                        sx={{
                                            '& .MuiBadge-badge': {
                                                fontSize: '0.8rem',
                                                height: '24px',
                                                minWidth: '45px'
                                            }
                                        }}
                                    >
                                        <Box
                                            onClick={openVideoTab}
                                            className="reference"
                                            sx={{
                                                ...(isEdit && {
                                                    border: '3px dotted'
                                                })
                                            }}
                                        >
                                            <img
                                                src={video['icon_url']}
                                                alt="video"
                                                id={video['id']}
                                                name={video['shortname']}
                                                width="60"
                                            />
                                            <Typography>{video['shortname']}</Typography>
                                        </Box>
                                    </Badge>
                                </Grid>
                            ))}
                            {isEdit && (
                                <Grid
                                    item
                                    xs={12 / (5 + videoData.length + 1)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <Box
                                        onClick={(e) => handleAddNew(e, 'Video')}
                                        className="reference"
                                        sx={{
                                            border: '3px dotted',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '70px',
                                            width: '70px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <AddIcon fontSize="large" color="primary" name="Video" />
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </SubCard>
                </Grid>
                <Grid item xs={12}>
                    <SubCard title="Documentation">
                        <Grid container>
                            <Grid item xs={12 / (12 + pdfData.length + (isEdit ? 1 : 0))} sx={{ textAlign: 'center' }}>
                                <Box onClick={handleDownloadFile} className="reference">
                                    <img src={pdfImg} alt="PDF" name="tech_specs" width="40" />
                                    <Typography>TechSpecs</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12 / (12 + pdfData.length + (isEdit ? 1 : 0))} sx={{ textAlign: 'center' }}>
                                <Box onClick={handleDownloadFile} className="reference">
                                    <img src={pdfImg} alt="PDF" name="welcome" width="40" />
                                    <Typography>Welcome!</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12 / (12 + pdfData.length + (isEdit ? 1 : 0))} sx={{ textAlign: 'center' }}>
                                <Box onClick={handleDownloadFile} className="reference">
                                    <img src={pdfImg} alt="PDF" name="security" width="40" />
                                    <Typography>Security</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12 / (12 + pdfData.length + (isEdit ? 1 : 0))} sx={{ textAlign: 'center' }}>
                                <Box onClick={handleDownloadFile} className="reference">
                                    <img src={docImg} alt="PDF" name="user_kit" width="40" />
                                    <Typography>User Kit</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12 / (12 + pdfData.length + (isEdit ? 1 : 0))} sx={{ textAlign: 'center' }}>
                                <Box onClick={handleDownloadFile} className="reference">
                                    <img src={pdfImg} alt="PDF" name="eula" width="40" />
                                    <Typography>EULA</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12 / (12 + pdfData.length + (isEdit ? 1 : 0))} sx={{ textAlign: 'center' }}>
                                <Box onClick={handleDownloadFile} className="reference">
                                    <img src={pdfImg} alt="PDF" name="sla" width="40" />
                                    <Typography>SLA</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12 / (12 + pdfData.length + (isEdit ? 1 : 0))} sx={{ textAlign: 'center' }}>
                                <Box onClick={handleDownloadFile} className="reference">
                                    <img src={pdfImg} alt="PDF" name="jamfguide" width="40" />
                                    <Typography>Using JAMF</Typography>
                                </Box>
                            </Grid>
                            {pluginData.sccm && (
                                <Grid item xs={12 / (12 + pdfData.length + (isEdit ? 1 : 0))} sx={{ textAlign: 'center' }}>
                                    <Box onClick={handleDownloadFile} className="reference">
                                        <img src={pdfImg} alt="PDF" name="sccm_guide" width="40" />
                                        <Typography>SCCM Guide</Typography>
                                    </Box>
                                </Grid>
                            )}
                            <Grid item xs={12 / (12 + pdfData.length + (isEdit ? 1 : 0))} sx={{ textAlign: 'center' }}>
                                <Box onClick={handleDownloadFile} className="reference">
                                    <img src={pdfImg} alt="PDF" name="api_guide" width="40" />
                                    <Typography>API Guide</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12 / (12 + pdfData.length + (isEdit ? 1 : 0))} sx={{ textAlign: 'center' }}>
                                <Box onClick={handleDownloadFile} className="reference">
                                    <img src={pdfImg} alt="PDF" name="wakeapp_guide" width="40" />
                                    <Typography>WakeApp Guide</Typography>
                                </Box>
                            </Grid>
                            {pdfData.map((pdf, key) => (
                                <Grid item xs={12 / (12 + pdfData.length + (isEdit ? 1 : 0))} sx={{ textAlign: 'center' }} key={key}>
                                    <Badge
                                        badgeContent="NEW"
                                        color="error"
                                        invisible={!pdf['is_new']}
                                        sx={{
                                            '& .MuiBadge-badge': {
                                                fontSize: '0.8rem',
                                                height: '24px',
                                                minWidth: '45px'
                                            }
                                        }}
                                    >
                                        <Box
                                            onClick={openPdfTab}
                                            className="reference"
                                            sx={{
                                                ...(isEdit && {
                                                    border: '3px dotted'
                                                })
                                            }}
                                        >
                                            <img src={pdf['icon_url']} alt="PDF" id={pdf['id']} name={pdf['shortname']} width="40" />
                                            <Typography>{pdf['shortname']}</Typography>
                                        </Box>
                                    </Badge>
                                </Grid>
                            ))}
                            {isEdit && (
                                <Grid
                                    item
                                    xs={12 / (12 + pdfData.length + 1)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <Box
                                        onClick={(e) => handleAddNew(e, 'PDF')}
                                        className="reference"
                                        sx={{
                                            border: '3px dotted',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '70px',
                                            width: '70px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <AddIcon fontSize="large" color="primary" name="PDF" />
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </SubCard>
                </Grid>
            </Grid>
            {!!selectedRef && (
                <EditVideoDialog
                    open={!!selectedRef}
                    onClose={() => setSelectedRef(null)}
                    reference={selectedRef}
                    refType={type}
                    onUpdate={handleUpdateReference}
                    onSave={handleSaveReference}
                    onDelete={handleDeleteReference}
                    limitFields={limitFields}
                />
            )}
        </MainCard>
    );
};

export default ManagementReference;
